/**
 * Shared presentational primitives for journey entry modules.
 *
 * Keeps every module visually consistent (the "paper over mist" look) without
 * coupling modules to each other. Modules import only what they need. The card
 * chrome itself lives in JourneyShell — modules render their inner content and
 * may use these helpers for headings, buttons, and recorded-state chips.
 */

export const J = {
  // Type label / eyebrow above an entry title (Montserrat, tracked, uppercase)
  eyebrow: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: 9, fontWeight: 500, letterSpacing: '0.34em', textTransform: 'uppercase',
    color: '#8ba6b1', marginBottom: 16,
  },
  // Entry title — uppercase light Montserrat with wide tracking (the brand's display voice;
  // Montserrat headers are uppercase-only per the design system).
  title: {
    fontFamily: '"Montserrat", sans-serif',
    fontSize: 'clamp(17px, 2.1vw, 24px)', fontWeight: 300, letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: '#07272D', lineHeight: 1.5, margin: 0,
  },
  // Body text container
  body: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 15, lineHeight: 1.75, color: '#3a5260',
  },
  // Mono meta (dates, counts)
  mono: {
    fontFamily: '"DM Mono", monospace', fontSize: 11, color: '#7a9aaa', letterSpacing: '0.02em',
  },
  rule: {
    height: '0.5px', background: 'rgba(7,39,45,0.1)', border: 'none', margin: '24px 0',
  },
  // Quiet tracked text link ("DOWNLOAD PDF", "OPEN") — the luxury alternative to a blue
  // underlined web link. Works on <a> and <button>; keep borderBottom AFTER border.
  textLink: {
    display: 'inline-block',
    fontFamily: '"Montserrat", sans-serif', fontSize: 8.5, fontWeight: 500,
    letterSpacing: '0.28em', textTransform: 'uppercase', color: '#6b8f95',
    textDecoration: 'none', background: 'none', cursor: 'pointer', padding: '0 0 4px',
    border: 'none', borderBottom: '0.5px solid rgba(7,39,45,0.22)',
    transition: 'color 0.15s, border-color 0.15s',
  },
}

export function PrimaryButton({ children, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600,
        padding: '10px 22px', borderRadius: 20, border: 'none', cursor: disabled ? 'default' : 'pointer',
        background: disabled ? 'rgba(7,39,45,0.25)' : '#07272D', color: '#fff',
        transition: 'background 0.15s',
      }}>
      {children}
    </button>
  )
}

export function GhostButton({ children, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600,
        padding: '10px 22px', borderRadius: 20, cursor: disabled ? 'default' : 'pointer',
        background: 'transparent', color: '#07272D', border: '0.5px solid rgba(7,39,45,0.3)',
      }}>
      {children}
    </button>
  )
}

/** A small "you already did this" confirmation chip. */
/** Shown to an owner's rep where the owner answers (docs/CLIENT_HUB_OWNERS_REP.md). */
export function ViewOnlyNote({ children }) {
  return (
    <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#6b7b80', fontStyle: 'italic' }}>
      {children || 'The owner answers this one. You can see it here to advise them.'}
    </div>
  )
}

export function DoneChip({ children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontFamily: '"DM Mono", monospace', fontSize: 11, fontWeight: 600,
      color: '#1D9E75', background: '#eaf8f2', padding: '4px 12px', borderRadius: 20,
    }}>
      <span style={{ fontSize: 12 }}>✓</span>{children}
    </span>
  )
}

/** Format a posted date ("YYYY-MM-DD" or ISO) → "Jun 22, 2026". Empty string if missing/invalid.
 *  Parses date-only strings as local (no timezone day-shift). */
export function fmtJourneyDate(d) {
  if (!d) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d)
  const dt = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d)
  if (isNaN(dt.getTime())) return ''
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** File-type badge color (mirrors SharePage). */
export function fileTypeColor(t) {
  const k = (t || '').toLowerCase()
  if (k.includes('pdf')) return { bg: '#fdf0ef', fg: '#c0392b' }
  if (k.includes('doc')) return { bg: '#e8f0f8', fg: '#1a6fa8' }
  if (k.includes('xls') || k.includes('sheet')) return { bg: '#eaf8f2', fg: '#1D9E75' }
  if (k.includes('ppt') || k.includes('slide')) return { bg: '#fef5eb', fg: '#b8540a' }
  if (k.includes('dwg') || k.includes('cad')) return { bg: '#f3edfb', fg: '#7c3aed' }
  return { bg: '#edf2f3', fg: '#7a9aaa' }
}
